import axios, { AxiosRequestHeaders } from 'axios';
import { cloneDeep } from 'lodash';
import { datadogLogs } from '@datadog/browser-logs';
import { getProcessEnvUrl } from '@/utils/getUrl';
import { LOGIN, LOGIN_CONFIRM } from '@/Router/routes';
import { ValidationError } from '.';
import { resolveIsLegacyLogin } from '@/utils/fetchData.ts';
import { createAuthUrl } from '@/utils/auth';

const LEGACY_LOGIN_API = 'LEGACY_LOGIN_API';
const WEB_API_URL = 'WEB_API_URL';

function getTokenFromSessionStorage() {
  return sessionStorage.getItem('token');
}

const axiosInstance = axios.create({
  baseURL: getProcessEnvUrl('WEB_API_URL'),
  withCredentials: true,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  } as AxiosRequestHeaders,
});

// This is a request interceptor that will run before every request.
// It will add the Authorization header with the token from sessionStorage.
axiosInstance.interceptors.request.use(
  async function getInternalAxiosRequestConfig(config) {
    const isLegacyLogin = await resolveIsLegacyLogin();
    const token = getTokenFromSessionStorage();

    const newConfig = cloneDeep(config);

    newConfig.baseURL = getProcessEnvUrl(
      isLegacyLogin ? LEGACY_LOGIN_API : WEB_API_URL,
    );

    if (!newConfig.headers) {
      newConfig.headers = {} as AxiosRequestHeaders;
    }

    if (newConfig.headers.Authorization) {
      return newConfig;
    }

    if (!token) {
      return newConfig;
    }

    if (isLegacyLogin) {
      newConfig.headers.Authorization = `Basic ${token}`;
    }

    return newConfig;
  },
);

axiosInstance.interceptors.response.use(
  function getInternalAxiosResponseConfig(response) {
    return response;
  },
  async function onRejected(error) {
    // 403 status code gets returned when client has been blocked via COLA
    if (error.response.status === 401 || error.response.status === 403) {
      // Wrong credentials from login endpoint return 401, returns error as normal error
      if (
        error.response.status === 401 &&
        error.response.request.responseURL.includes('/token/basic')
      ) {
        return Promise.reject(error);
      }

      sessionStorage.clear();
      const isLegacyLogin = await resolveIsLegacyLogin();
      if (isLegacyLogin) {
        window.location.replace(`${LOGIN}?sessionExpired=true`);
      } else {
        window.location.replace(createAuthUrl(LOGIN_CONFIRM));
      }
    }

    if (error.response.status === 400 && error.response.data.errors) {
      const validationError = new ValidationError(error.response.data.errors);
      return Promise.reject(validationError);
    }

    if (error.response.status >= 500) {
      datadogLogs.logger.error('[Axios interceptor] webapi error', error);
    }

    return Promise.reject(error);
  },
);

export default axiosInstance;
